/**
 * CentroDireitosPage — Hub central do Centro de Direitos do Titular (DPO Tenant).
 *
 * Brief 5 B1 — "A tela mais delicada do produto."
 *
 * Padrões obrigatórios:
 *   - Opção A (Capability-driven UI): toda ação gated por capability específica.
 *   - D-A 3-camadas (desktop): tier + role + capability.
 *   - D-B defense-in-depth: cada sub-componente sensível tem próprio gate.
 *   - 6 princípios LGPD §1.2 materializados visualmente.
 *
 * Gate principal: `lgpd-requests-read` (papel DPO — role-mapping DPO-only,
 * governança 18/Mai/2026; admin_tenant NÃO acessa o Centro).
 * Solo tier: acesso restrito — banner informativo + funcionalidades limitadas.
 * Suspended tenant: DPO mantém acesso integral ao Centro (§1.2 item 4).
 *
 * P15 minimização: CPF mascarado em listagens; sem dados sensíveis fora Drawer.
 * P17 auditabilidade: audit trail via LgpdCascadePreview sem gaps.
 *
 * ADRs: ADR-024 + ADR-098 + ADR-110 + ADR-107.
 * Sprint Frontend Desktop W3.2 (PR #67).
 */

import { CapabilityLockedBanner } from '@lume/ui-components';
import { useState } from 'react';

import { CapabilityGate } from '../components/CapabilityGate';
import { AnonimizacaoWizard } from '../features/dpo/centro-direitos/AnonimizacaoWizard';
import { AuthorizedContactsListView } from '../features/dpo/centro-direitos/AuthorizedContactsListView';
import { CreateLgpdRequestModal } from '../features/dpo/centro-direitos/CreateLgpdRequestModal';
import { LegalHoldsListView } from '../features/dpo/centro-direitos/LegalHoldsListView';
import { LgpdKpiStrip } from '../features/dpo/centro-direitos/LgpdKpiStrip';
import { LgpdRequestDetailDrawer } from '../features/dpo/centro-direitos/LgpdRequestDetailDrawer';
import { LgpdRequestsListView } from '../features/dpo/centro-direitos/LgpdRequestsListView';
import { OppositionsListView } from '../features/dpo/centro-direitos/OppositionsListView';
import { useCapability } from '../hooks/use-capability';
import { useLegalHolds } from '../hooks/use-lgpd-legal-holds';
import { useLgpdRequests } from '../hooks/use-lgpd-requests';

import type {
  LgpdRequestStatus,
  LgpdRequestType,
  LgpdRequestWireItem,
} from '../features/dpo/centro-direitos/types';

// ─── Tab IDs ──────────────────────────────────────────────────────────────────

type CentroTab = 'requests' | 'legalholds' | 'authorized' | 'oppositions' | 'templates';

const TABS: { id: CentroTab; label: string; capability?: string }[] = [
  { id: 'requests', label: 'Solicitações' },
  { id: 'legalholds', label: 'Legal Holds', capability: 'lgpd-legal-holds-manage' },
  { id: 'authorized', label: 'Contatos Autorizados', capability: 'lgpd-authorized-contacts-read' },
  { id: 'oppositions', label: 'Oposições' },
  { id: 'templates', label: 'Templates' },
];

// ─── CentroDireitosPage ───────────────────────────────────────────────────────

export function CentroDireitosPage(): JSX.Element {
  const canAccessCenter = useCapability('lgpd-requests-read');

  // D-B own gate — não confiar em rota protegida
  if (!canAccessCenter) {
    return (
      <div style={{ padding: 32 }}>
        <CapabilityLockedBanner
          capability="lgpd-requests-read"
          tierMin="professional"
          fallbackMessage="O Centro de Direitos do Titular é restrito ao DPO do tenant."
        />
      </div>
    );
  }

  return <CentroDireitosPageInner />;
}

// ─── Inner (rendered when capability granted) ─────────────────────────────────

function CentroDireitosPageInner(): JSX.Element {
  const [activeTab, setActiveTab] = useState<CentroTab>('requests');

  // Requests filter state
  const [filterStatus, setFilterStatus] = useState<LgpdRequestStatus | 'all'>('all');
  const [filterType, setFilterType] = useState<LgpdRequestType | 'all'>('all');

  // Drawer state — null = closed
  const [selectedRequest, setSelectedRequest] = useState<LgpdRequestWireItem | null>(null);

  // Wizard state — null = closed
  const [wizardRequest, setWizardRequest] = useState<LgpdRequestWireItem | null>(null);

  // Modal de criação manual (Item 362)
  const [createOpen, setCreateOpen] = useState(false);

  // Requests data (for filtered list)
  const statusFilter = filterStatus === 'all' ? undefined : filterStatus;
  const typeFilter = filterType === 'all' ? undefined : filterType;
  const {
    data: allRequests,
    isLoading,
    isError,
  } = useLgpdRequests({
    ...(statusFilter !== undefined ? { status: statusFilter } : {}),
    ...(typeFilter !== undefined ? { requestType: typeFilter } : {}),
  });

  // For KPI strip, always get all (no filter)
  const { data: allRequestsUnfiltered } = useLgpdRequests({});
  // Legal holds for KPI (active count) — D-B: capability check in LegalHoldsListView
  const { data: allLegalHolds } = useLegalHolds({});

  function handleOpenDetail(request: LgpdRequestWireItem): void {
    setSelectedRequest(request);
  }

  function handleCloseDrawer(): void {
    setSelectedRequest(null);
  }

  function handleOpenWizard(request: LgpdRequestWireItem): void {
    setWizardRequest(request);
    setSelectedRequest(null); // close drawer while wizard runs
  }

  function handleCloseWizard(): void {
    setWizardRequest(null);
  }

  function handleWizardSuccess(): void {
    setWizardRequest(null);
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Page header */}
      <div
        style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#fff',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: '#111827',
                margin: 0,
              }}
            >
              Centro de Direitos do Titular
            </h1>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
              Gestão de solicitações LGPD (Art. 18) · Conformidade do DPO
            </p>
          </div>

          {/* Ações DPO — registro manual de pedido recebido por canal externo (Item 362) */}
          <CapabilityGate capability="lgpd-requests-create">
            <button
              type="button"
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                backgroundColor: '#4f46e5',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              + Nova solicitação
            </button>
          </CapabilityGate>
        </div>

        {/* LGPD principles banner — 6 princípios §1.2 */}
        <LgpdPrinciplesBanner />
      </div>

      {/* KPI Strip */}
      <div
        style={{
          padding: '12px 24px',
          backgroundColor: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          flexShrink: 0,
        }}
      >
        <LgpdKpiStrip requests={allRequestsUnfiltered} legalHolds={allLegalHolds} />
      </div>

      {/* Tab navigation */}
      <div
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#fff',
          padding: '0 24px',
          flexShrink: 0,
        }}
        role="tablist"
        aria-label="Abas Centro de Direitos"
      >
        {TABS.map((tab) => (
          <TabButton
            key={tab.id}
            tab={tab}
            isActive={activeTab === tab.id}
            onClick={() => {
              setActiveTab(tab.id);
            }}
          />
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        {activeTab === 'requests' && (
          <LgpdRequestsListView
            requests={allRequests}
            isLoading={isLoading}
            isError={isError}
            filterStatus={filterStatus}
            filterType={filterType}
            onFilterStatusChange={setFilterStatus}
            onFilterTypeChange={setFilterType}
            onOpenDetail={handleOpenDetail}
          />
        )}

        {activeTab === 'legalholds' && <LegalHoldsListView filterStatus="active" />}

        {activeTab === 'authorized' && <AuthorizedContactsListView />}

        {activeTab === 'oppositions' && <OppositionsListView onOpenDetail={handleOpenDetail} />}

        {activeTab === 'templates' && <TemplatesPlaceholder />}
      </div>

      {/* Drawer de detalhe de solicitação — isOpen gerenciado internamente (request !== null) */}
      <LgpdRequestDetailDrawer
        request={selectedRequest}
        onClose={handleCloseDrawer}
        onInitiateAnonymization={handleOpenWizard}
      />

      {/* Wizard de anonimização */}
      {wizardRequest !== null && (
        <AnonimizacaoWizard
          request={wizardRequest}
          onClose={handleCloseWizard}
          onSuccess={handleWizardSuccess}
        />
      )}

      {/* Registro manual de solicitação (Item 362) — a lista invalida no hook */}
      {createOpen && (
        <CreateLgpdRequestModal
          onClose={() => {
            setCreateOpen(false);
          }}
          onCreated={() => {
            setCreateOpen(false);
            setActiveTab('requests');
          }}
        />
      )}
    </div>
  );
}

// ─── Tab Button ───────────────────────────────────────────────────────────────

function TabButton({
  tab,
  isActive,
  onClick,
}: {
  tab: (typeof TABS)[number];
  isActive: boolean;
  onClick: () => void;
}): JSX.Element {
  // Always call useCapability unconditionally (hooks rules compliance).
  // If no capability required, use empty string — hook returns false for
  // unknown capabilities, but we override below when no capability required.
  const capabilityResult = useCapability(tab.capability ?? '');
  const hasCapability = tab.capability === undefined ? true : capabilityResult;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      disabled={!hasCapability}
      onClick={onClick}
      style={{
        padding: '10px 16px',
        fontSize: 13,
        fontWeight: isActive ? 600 : 400,
        color: !hasCapability ? '#d1d5db' : isActive ? '#4f46e5' : '#6b7280',
        border: 'none',
        borderBottom: isActive ? '2px solid #4f46e5' : '2px solid transparent',
        backgroundColor: 'transparent',
        cursor: hasCapability ? 'pointer' : 'not-allowed',
        whiteSpace: 'nowrap',
        transition: 'color 0.1s, border-color 0.1s',
      }}
    >
      {tab.label}
      {!hasCapability && (
        <span
          style={{
            marginLeft: 6,
            fontSize: 9,
            color: '#d1d5db',
            fontWeight: 400,
            textTransform: 'uppercase',
          }}
        >
          PRO+
        </span>
      )}
    </button>
  );
}

// ─── LGPD Principles Banner ───────────────────────────────────────────────────

const LGPD_PRINCIPLES = [
  { id: 1, label: 'Finalidade', short: 'Art. 6, I' },
  { id: 2, label: 'Adequação', short: 'Art. 6, II' },
  { id: 3, label: 'Necessidade', short: 'Art. 6, III' },
  { id: 4, label: 'Livre acesso', short: 'Art. 18, I' },
  { id: 5, label: 'Qualidade', short: 'Art. 6, V' },
  { id: 6, label: 'Segurança', short: 'Art. 6, VII' },
];

function LgpdPrinciplesBanner(): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        marginTop: 12,
        flexWrap: 'wrap',
      }}
      aria-label="Princípios LGPD aplicados a esta tela"
      role="list"
    >
      {LGPD_PRINCIPLES.map((p) => (
        <div
          key={p.id}
          role="listitem"
          title={`Princípio LGPD ${p.id}: ${p.label} (${p.short})`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 8px',
            borderRadius: 20,
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            fontSize: 10,
            fontWeight: 500,
            color: '#1d4ed8',
          }}
        >
          <span style={{ fontWeight: 700 }}>P{p.id}</span>
          <span>{p.label}</span>
          <span style={{ color: '#93c5fd', fontSize: 9 }}>{p.short}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Placeholder tabs ─────────────────────────────────────────────────────────

function TemplatesPlaceholder(): JSX.Element {
  return (
    <div
      style={{
        padding: '48px 24px',
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: 13,
      }}
    >
      <div style={{ fontSize: 32, marginBottom: 12 }}>📄</div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Templates de Resposta</div>
      <div style={{ fontSize: 12 }}>
        Templates pré-formatados para respostas LGPD (prazo 15 dias corridos Art. 19). Customizáveis
        por tipo de solicitação.
      </div>
    </div>
  );
}
